import { ReactNode, FC, ButtonHTMLAttributes } from "react";
import classNames from "classnames";
import {
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PencilSquareIcon,
} from "@heroicons/react/20/solid";

const buttonClassname =
  "inline-flex justify-center rounded-md px-3 py-2 " +
  "text-sm font-semibold shadow-sm " +
  "focus-visible:outline focus-visible:outline-2 " +
  "focus-visible:outline-offset-2 border-2 border-indigo-600";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
}
export const PrimaryButton: FC<ButtonProps> = ({
  children,
  className,
  disabled,
  ...props
}) => {
  return (
    <button
      disabled={disabled}
      className={classNames(
        buttonClassname,
        "bg-indigo-600 hover:bg-indigo-500 text-white focus-visible:outline-indigo-500",
        className,
        // Sin pointer-events-none: el atributo `disabled` ya bloquea la
        // activación, y suprimir los eventos de puntero también mata el cursor
        // de "no permitido" y cualquier tooltip que explique POR QUÉ está
        // deshabilitado. Mismo criterio que LinkText.
        disabled ? "opacity-50 cursor-not-allowed" : ""
      )}
      {...props}
    >
      {children}
    </button>
  );
};

export const SecondaryButton: FC<ButtonProps> = ({
  children,
  className,
  disabled,
  ...props
}) => {
  return (
    <button
      disabled={disabled}
      className={classNames(
        buttonClassname,
        className,
        disabled ? "opacity-50 cursor-not-allowed" : "",
        "bg-white hover:bg-gray-200 focus-visible:outline-indigo-500 text-indigo-600"
      )}
      {...props}
    >
      {children}
    </button>
  );
};

interface NextButtonProps extends ButtonProps {}
export const NextButton: FC<NextButtonProps> = ({ className, ...props }) => {
  return (
    <PrimaryButton {...props} className={`pl-4 ${className}`}>
      Siguiente
      <ChevronRightIcon
        className="h-5 w-5 flex-none text-gray-400"
        aria-hidden="true"
      />
    </PrimaryButton>
  );
};

interface BackButtonProps extends ButtonProps {
  handleOnRegret: () => void;
}
export const BackButton: FC<BackButtonProps> = ({
  handleOnRegret,
  ...props
}) => {
  return (
    <PrimaryButton onClick={() => handleOnRegret()} className="pr-4" {...props}>
      <ChevronLeftIcon
        className="h-5 w-5 flex-none text-gray-400"
        aria-hidden="true"
      />
      Regresar
    </PrimaryButton>
  );
};

interface AddButtonProps extends ButtonProps {}
export const AddButton: FC<AddButtonProps> = ({ ...props }) => {
  return (
    <PrimaryButton {...props} className="pr-4">
      <PlusIcon className="h-5 w-5 flex-none text-white" aria-hidden="true" />
      Agregar
    </PrimaryButton>
  );
};

interface EditButtonProps extends ButtonProps {}
export const EditButton: FC<EditButtonProps> = ({ ...props }) => {
  return (
    <PrimaryButton {...props} className="pr-4">
      <PencilSquareIcon
        className="mr-1 h-5 w-5 flex-none text-white"
        aria-hidden="true"
      />
      Editar
    </PrimaryButton>
  );
};

interface LinkTextProps {
  text: string;
  disabled?: boolean;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}
/**
 * Enlace de acción. Es un <button> y no un <span> porque en varias pantallas es
 * la única salida de un bloqueo, y un span no recibe foco: quien trabaja con
 * teclado o lector de pantalla quedaba encerrado.
 *
 * Dos detalles que no son cosméticos:
 *
 *  - `disabled` va al atributo real, no sólo a `pointer-events-none`. Ese truco
 *    bloquea el ratón y nada más; como <button> el elemento seguiría siendo
 *    tabulable y activable con Enter. Dos usos son compuertas de permisos
 *    (PaymentList: retenciones según rol; PaymentTotal: crédito disponible),
 *    así que el atributo nativo es lo que hace real el bloqueo.
 *  - `text-left`: el preflight de Tailwind resetea fondo, borde, fuente,
 *    márgenes y relleno de <button>, pero NO `text-align`, cuyo default de
 *    navegador es `center`. Sin esta clase, cualquier uso cuyo texto envuelva a
 *    dos líneas empezaría a centrarse solo.
 *
 * El idioma de foco es el mismo de `buttonClassname` arriba: outline, no ring.
 */
export const LinkText: FC<LinkTextProps> = ({
  text,
  disabled,
  className,
  onClick,
}) => {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={classNames(
        "text-left text-blue-500",
        "focus-visible:outline focus-visible:outline-2",
        "focus-visible:outline-offset-2 focus-visible:outline-indigo-600",
        disabled ? "opacity-50" : "hover:underline hover:cursor-pointer",
        className
      )}
    >
      {text}
    </button>
  );
};
